import React from 'react';

import {
  Box,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import { Spacer, Text } from '../../../components';
import { TeamData } from '../../../models/Team';
import { TextType } from '../../../models/Text';

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamData | null;
}

const richTextSx = {
  fontFamily: 'inherit',
  color: 'gray.500',
  fontWeight: 300,
  letterSpacing: '1.5px',
  lineHeight: 'normal',
  fontSize: { base: '12px', md: '14px' },

  '& *': {
    fontFamily: 'inherit',
    color: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
  },

  '& div, & span': {
    margin: 0,
    padding: 0,
  },

  '& p': {
    margin: 0,
  },

  '& p + p': {
    marginTop: '12px',
  },

  '& ul, & ol': {
    paddingLeft: '18px',
    margin: 0,
    marginTop: '12px',
    marginBottom: '12px',
  },

  '& li': {
    marginBottom: '8px',
  },

  '& strong, & b': {
    fontWeight: 700,
  },

  '& em, & i': {
    fontStyle: 'italic',
  },

  '& a': {
    textDecoration: 'none',
    fontWeight: 700,
  },

  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 700,
    margin: 0,
    marginTop: '14px',
    marginBottom: '10px',
  },
};

const TeamMemberModal = ({
  isOpen,
  onClose,
  member,
}: TeamMemberModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay />

      <ModalContent
        maxW="60vw"
        maxH="75vh"
        minH="35vh"
        minW="55vw"
        borderRadius="10px"
        overflow="hidden"
        paddingTop={{ base: '30px', md: '40px' }}
        paddingBottom={{ base: '30px', md: '40px' }}
      >
        <ModalCloseButton
          zIndex={10}
          top="10px"
          right="10px"
        />

        <ModalBody
          padding={0}
          overflow="hidden"
        >
          <Box
            maxH={{
              base: 'calc(75vh - 60px)',
              md: 'calc(75vh - 80px)',
            }}
            overflowY="auto"
            paddingX={{ base: '24px', md: '40px' }}
          >
            {member ? (
              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '24px', md: '40px' }}
              >
                {/* IMAGE */}
                <Box
                  width={{ base: '100%', md: '38%' }}
                  flexShrink={0}
                >
                  <Image
                    src={member.image}
                    alt={member.name}
                    width="100%"
                    borderRadius="8px"
                    objectFit="cover"
                  />
                </Box>

                {/* CONTENT */}
                <Box width={{ base: '100%', md: '62%' }}>
                  <Text type={TextType.Inter18Bold}>
                    {member.name}
                  </Text>

                  {member.role ? (
                    <>
                      <Spacer size={10} />
                      <Text type={TextType.Inter14Light}>
                        {member.role}
                      </Text>
                    </>
                  ) : null}


                  {member.biography ? (
                    <>
                      <Spacer size={20} />
                      <Box
                        sx={richTextSx}
                        dangerouslySetInnerHTML={{
                          __html: member.biography,
                        }}
                      />
                    </>
                  ) : null}

                  {member.education ? (
                    <>
                      <Spacer size={28} />

                      <Text type={TextType.Inter14Bold}>
                        Education, Awards and Certifications
                      </Text>

                      <Spacer size={14} />

                      <Box
                        sx={richTextSx}
                        dangerouslySetInnerHTML={{
                          __html: member.education,
                        }}
                      />
                    </>
                  ) : null}
                </Box>
              </Flex>
            ) : null}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TeamMemberModal;